import { useState, useCallback, useMemo } from 'react';
import { useQuery, keepPreviousData, QueryKey } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import type { PaginationState } from '@tanstack/react-table';
import { errorStatus } from '@/lib/translateBackendError';

/**
 * Generic paginated response type for API responses.
 * All paginated endpoints should return this structure.
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Options for the usePaginatedQuery hook
 */
export interface UsePaginatedQueryOptions<T> {
  /** Query key factory - receives pagination params */
  queryKey: (pagination: { limit: number; offset: number }) => QueryKey;
  /** Query function - receives limit and offset, returns paginated response */
  queryFn: (limit: number, offset: number) => Promise<PaginatedResponse<T>>;
  /** Initial page size (default: 50) */
  initialPageSize?: number;
  /** Initial page index (default: 0) */
  initialPageIndex?: number;
  /**
   * Controlled page state, for a page that keeps it elsewhere (the URL). The
   * query then reads it directly, so a deep link to page N fetches page N on
   * the first render. The caller owns the setter; `onPaginationChange` and the
   * initial values are not used.
   */
  pagination?: PaginationState;
  /** Stale time in milliseconds (default: 5 minutes) */
  staleTime?: number;
  /** Whether the query is enabled (default: based on auth state) */
  enabled?: boolean;
}

/**
 * Return type for the usePaginatedQuery hook
 */
export interface UsePaginatedQueryResult<T> {
  /** The paginated data items */
  data: T[];
  /** Total number of items across all pages */
  total: number;
  /** Whether the initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress (including background) */
  isFetching: boolean;
  /** Whether we're showing placeholder data from a previous page */
  isPlaceholderData: boolean;
  /** Current pagination state for TanStack Table */
  pagination: PaginationState;
  /** Total number of pages */
  pageCount: number;
  /** Handler for pagination changes - pass to TanStack Table's onPaginationChange */
  onPaginationChange: (updater: PaginationState | ((old: PaginationState) => PaginationState)) => void;
  /** Error if the query failed */
  error: Error | null;
  /** Refetch the current page */
  refetch: () => void;
  /**
   * The raw response body, so a caller can read endpoint-specific fields that
   * sit ALONGSIDE the four standard pagination keys -- for example the prospect
   * list's `overdue_followup_count`. Purely additive: `data` and `total` keep
   * their existing meaning and no existing caller needs to change.
   */
  response: PaginatedResponse<T> | undefined;
}

/**
 * A reusable hook for server-side paginated queries.
 *
 * Uses TanStack Query with `keepPreviousData` for smooth pagination transitions.
 * Designed to work seamlessly with TanStack Table's server-side pagination.
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   total,
 *   isLoading,
 *   pagination,
 *   pageCount,
 *   onPaginationChange,
 * } = usePaginatedQuery({
 *   queryKey: (p) => ['children', 'list', p],
 *   queryFn: (limit, offset) => ChildrenService.getApiV1Children(limit, offset),
 *   initialPageSize: 50,
 * });
 *
 * // Use with BaseEditableTable
 * <BaseEditableTable
 *   data={data}
 *   serverSidePagination
 *   totalRows={total}
 *   pagination={pagination}
 *   onPaginationChange={onPaginationChange}
 * />
 * ```
 */
export function usePaginatedQuery<T>({
  queryKey,
  queryFn,
  initialPageSize = 50,
  initialPageIndex = 0,
  pagination: controlledPagination,
  staleTime = 5 * 60 * 1000, // 5 minutes
  enabled: enabledOverride,
}: UsePaginatedQueryOptions<T>): UsePaginatedQueryResult<T> {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  // Pagination state, unless the caller controls it
  const [ownPagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });
  const pagination = controlledPagination ?? ownPagination;

  // Calculate offset from pagination state
  const offset = pagination.pageIndex * pagination.pageSize;
  const limit = pagination.pageSize;

  // Determine if query is enabled. The caller's override can only NARROW: auth
  // must always be ready before an authenticated fetch fires, so a caller passing
  // `enabled: true` can never bypass the auth gate (it only re-affirms the default).
  const isEnabled = (enabledOverride ?? true) && isSignedIn && isAuthReady;

  // Execute the paginated query
  const {
    data: response,
    isLoading,
    isFetching,
    isPlaceholderData,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKey({ limit, offset }),
    queryFn: () => queryFn(limit, offset),
    enabled: isEnabled,
    staleTime,
    // Keep previous data visible while fetching new page
    placeholderData: keepPreviousData,
    retry: (failureCount, err) => {
      // A 4xx answer (a rejected filter, no access) will not change on retry.
      const status = errorStatus(err);
      if (status !== undefined && status >= 400 && status < 500) return false;
      // Errors without a status: don't retry on auth errors
      if (err instanceof Error && (
        err.message.includes('401') ||
        err.message.includes('403') ||
        err.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Calculate page count from total
  const pageCount = useMemo(() => {
    if (!response?.total) return 0;
    return Math.ceil(response.total / pagination.pageSize);
  }, [response?.total, pagination.pageSize]);

  // Handle pagination changes
  const onPaginationChange = useCallback(
    (updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
      setPagination((old) => {
        const newPagination = typeof updater === 'function' ? updater(old) : updater;
        return newPagination;
      });
    },
    []
  );

  return {
    data: response?.data ?? [],
    total: response?.total ?? 0,
    isLoading,
    isFetching,
    isPlaceholderData,
    pagination,
    pageCount,
    onPaginationChange,
    error: error as Error | null,
    refetch,
    response,
  };
}

/**
 * Helper type for creating paginated query key factories.
 * Use with entity-specific query key patterns.
 *
 * @example
 * ```tsx
 * export const childKeys = {
 *   all: ['children'] as const,
 *   lists: () => [...childKeys.all, 'list'] as const,
 *   listPaginated: (p: PaginationParams) => [...childKeys.lists(), p] as const,
 *   // ... other keys
 * };
 * ```
 */
export interface PaginationParams {
  limit: number;
  offset: number;
}
